// Carry Roll HelpTooltip — per-parameter help (11.8).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';

interface HelpTooltipProps {
  label: string;
  children: ReactNode;
}

type Placement = 'above' | 'below';

export function HelpTooltip({ label, children }: HelpTooltipProps) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>('above');
  // Horizontal pixel offset used to keep the popover within the viewport when
  // the trigger sits near the left/right edge (shift/clamp). Positive shifts
  // right, negative shifts left.
  const [shiftX, setShiftX] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLSpanElement | null>(null);

  // Close on Escape so keyboard users aren't trapped with the popover open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Re-measure placement after the popover paints so we can flip/clamp before
  // the user perceives a clipped tooltip. useLayoutEffect avoids a flash of the
  // wrong placement.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const reposition = () => {
      const tg = trigger.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      const margin = 8; // breathing room from the viewport edge

      // Vertical: flip below if there isn't room above.
      let nextPlacement: Placement = 'above';
      const roomAbove = tg.top - tipRect.height - 4;
      const roomBelow = window.innerHeight - tg.bottom - tipRect.height - 4;
      if (roomAbove < margin && roomBelow >= roomAbove) {
        nextPlacement = 'below';
      }
      setPlacement((prev) => (prev === nextPlacement ? prev : nextPlacement));

      // Horizontal: clamp so the popover stays in-view. Keep the trigger's
      // center under the tooltip's center unless that would clip.
      const desiredLeft = tg.left + tg.width / 2 - tipRect.width / 2;
      const maxLeft = window.innerWidth - tipRect.width - margin;
      const minLeft = margin;
      const clampedLeft = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
      // Convert to an offset relative to the default centered transform.
      const centeredLeft = tg.left + tg.width / 2 - tipRect.width / 2;
      const offset = clampedLeft - centeredLeft;
      setShiftX((prev) => (prev === offset ? prev : offset));
    };

    reposition();
    // Re-measure on viewport changes (scroll within a scroll container, resize,
    // or dynamic reflow) so an open tooltip stays anchored correctly.
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  return (
    <span className="inline-flex items-center" style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Help for ${label}`}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
          transition: 'color var(--motion-fast)',
          marginLeft: '0.2em',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--color-accent)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--color-text-muted)';
        }}
      >
        <HelpCircle size={14} aria-hidden="true" />
      </button>
      {open && (
        <span
          ref={tipRef}
          role="tooltip"
          className="tooltip-pop text-xs"
          style={{
            position: 'absolute',
            display: 'block',
            zIndex: 10,
            ...(placement === 'above'
              ? { bottom: '100%', marginBottom: '0.6rem' }
              : { top: '100%', marginTop: '0.6rem' }),
            left: '50%',
            transform: `translateX(calc(-50% + ${shiftX}px))`,
            width: 'max-content',
            maxWidth: '200px',
            whiteSpace: 'normal',
            padding: 'var(--space-2)',
            backgroundColor: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
